import { Box, Stepper as MantineStepper } from '@mantine/core'
import { Site, SITE_ENV_LABELS, SITE_ENVS, SiteEnv } from '@pubstack/common/src/site'
import { ClipboardEvent, FunctionComponent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useCreateSites } from '~/api/app/sites.api'
import { Sizes } from '~/assets/style/tokens'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Select } from '~/components/Select'
import { useToast } from '~/components/Toast/useToasts'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useProduct } from '~/hooks/useProduct'
import { useScope } from '~/hooks/useScope'
import { scopeOwnsOffer } from '~/modules/monetization/upgrade/scopeOffers'
import { useStep } from '~/utils/useStep'
import { useStepperFocus, validateStepOnEnter } from '~/utils/validateStepOnEnter'
import { parseMediaNames, rowsAfterPaste } from './parseMediaNames'
import { CreateSiteStepReview } from './CreateSiteStepReview'
import { SiteProductPicker } from './SiteProductPicker'
import { SiteSaveConfirmationModal } from './SiteSaveConfirmationModal'
import {
  availableSiteEnvs,
  availableSiteProducts,
  isActivatingProduct,
  PRODUCT_IS_FINAL,
  PRODUCT_IS_FINAL_TITLE,
  SiteProduct,
  siteEnvDisabledReason,
  siteProductDisabledReason,
  siteProductPayload,
} from './siteProduct'

/** `env` is unset until picked: on Node a media can be anything, and there is no environment to assume. */
type MediaRow = { name: string; env?: SiteEnv }
type CreateMediaForm = { sites: MediaRow[]; product: SiteProduct }

const STEP_COUNT = 3
const PRODUCT_STEP = 1
const MEDIAS_STEP = 2
const REVIEW_STEP = 3

/** The rows that will actually be created: the trailing free row and anything blank are not medias. */
const namedRows = (sites: MediaRow[]): MediaRow[] => sites.map((site) => ({ ...site, name: site.name.trim() })).filter((site) => site.name.length > 0)

/**
 * The environment a fresh row starts on.
 *
 * Nothing is assumed where there is a real choice — on Node a media is as likely to be an app as a site.
 * Where the product leaves one legal environment there is nothing to ask, so the row is given it rather
 * than made to hold up the form on a question with a single answer.
 */
const defaultEnvFor = (product: SiteProduct): SiteEnv | undefined => {
  const envs = availableSiteEnvs(product)
  return envs.length === 1 ? envs[0] : undefined
}

type Props = {
  scopeId: string
  /** Every media created, not just the first: what the caller does next depends on how many there are. */
  onClose: (sites: Site[]) => void
}

/**
 * Add one media, or the whole list at once.
 *
 * Three steps, the same shape as creating a bidder seat: what the medias will run on, the medias
 * themselves, and a look at both before anything is created. The review earns its step because putting a
 * media on One or Node is one-way, and a pasted list of twenty is exactly the case where nobody re-reads
 * what they pasted.
 *
 * The product is asked first because it is the constraint, not because it reads better there: One is
 * client-side Prebid and Analytics measures the web on its own, so either of them makes every media in
 * the batch a web media, and only Node leaves an environment to pick. Asking it first means the illegal
 * pair — a web-only product against an AMP media — is never offered, where asking it last would let a
 * list be built across four environments and then have the product silently rule most of it out.
 *
 * The environment then belongs to the media rather than to the batch, one line per media, because a
 * publisher onboarding an inventory onto Node has web, iOS and AMP in the same list. The name field takes
 * a paste of the lot — a spreadsheet column or a comma-separated list — and every row it opens keeps the
 * environment of the row it was pasted into, so a batch that is all one environment still costs one pick.
 */
export const CreateSiteFlyout: FunctionComponent<Props> = ({ scopeId, onClose }) => {
  // Hooks
  const [batchError, setBatchError] = useState('')
  const user = useAuthenticatedUser()
  const { product } = useProduct()
  const modal = useGlobalModal()
  const [currentStep, stepHelpers] = useStep(STEP_COUNT)
  const bodyRef = useStepperFocus(currentStep)
  const rowsRef = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const scope = useScope()
  // Enabling a product is one-way, so it stays an owner's call.
  const canEnableProduct = user.getScopeRole() === 'owner' || user.isAdmin
  const scopeHasOne = scopeOwnsOffer(scope, 'one')
  const { hasNode: scopeHasNode, isStaffOnNode } = useNodeStaffAccess()

  // `env: 'web'` is not a claim about the batch — it is what asking the product first means. The two
  // web-only rules live on the environment side now (`siteEnvDisabledReason`), and what is left for the
  // product cards to say is what no environment can fix: an offer the scope never bought, a role the
  // viewer lacks. Web is the environment every product admits, so it is the one that asks only those.
  const availability = { env: 'web' as SiteEnv, scopeHasOne, scopeHasNode, canEnableProduct }

  // Pre-select the product of the list this flyout was opened from — but only if it is one this scope can
  // actually be given. `useProduct` falls back to One off a product path, so on a Node-only scope the
  // pre-selection would otherwise open the flyout on a card that is closed, with nothing to say why Add
  // never lights up. Nothing pickable at all leaves the pre-selection standing, closed and explained.
  const preselectedProduct: SiteProduct = canEnableProduct ? product : 'analytics'
  const initialProduct: SiteProduct = siteProductDisabledReason(preselectedProduct, availability) ? (availableSiteProducts(availability)[0] ?? preselectedProduct) : preselectedProduct

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    trigger,
    formState: { errors, isValid },
  } = useForm<CreateMediaForm>({
    defaultValues: { sites: [{ name: '', env: defaultEnvFor(initialProduct) }], product: initialProduct },
    mode: 'onChange',
  })

  const { fields, remove, append, replace } = useFieldArray({ control, name: 'sites' })
  const watchedSites = useWatch({ control, name: 'sites' })
  const selectedProduct = useWatch({ control, name: 'product' })
  const medias = namedRows(watchedSites ?? [])
  const names = medias.map((media) => media.name)
  const selectedProductReason = siteProductDisabledReason(selectedProduct, availability)

  // Keep one empty row at the end: as soon as the last one is named, a fresh one opens under it, so
  // adding a second media never costs a click on an "add a row" button. It inherits the environment of
  // the row above for the same reason a paste does — a list is usually all one environment.
  useEffect(() => {
    const sites = watchedSites ?? []
    const last = sites[sites.length - 1]
    if (sites.length === 0 || last?.name) {
      append({ name: '', env: last?.env ?? defaultEnvFor(getValues('product')) }, { shouldFocus: false })
    }
    // Re-validate every name on each edit, so a duplicate is flagged — and cleared — on both rows
    // holding it rather than only on the one being typed in.
    trigger('sites')
  }, [watchedSites, append, getValues, trigger])

  // The product moves the environments, never the other way round: this is the one direction, and the
  // mirror of it would only fight this one. A row on an environment the new product cannot take is moved
  // to the one it can, rather than left holding a pair that would be refused on save.
  useEffect(() => {
    const envs = availableSiteEnvs(selectedProduct)
    getValues('sites').forEach((row, index) => {
      // On a product with a single legal environment there is nothing left to ask, so even a row that
      // had not been given one yet is given it — it is an answer, not a default standing in for one.
      const next = envs.length === 1 || (row.env && siteEnvDisabledReason(row.env, selectedProduct)) ? envs[0] : row.env
      if (next !== row.env) {
        setValue(`sites.${index}.env`, next, { shouldDirty: true })
      }
    })
    // Only the product moves this on: re-running it on every keystroke would fight the user's own pick.
    // oxlint-disable-next-line react/rule-suppression -- the narrowed dep list below is deliberate, and the compiler skips the component rather than accept it
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct])

  // API Hooks
  const { mutateAsync: createSites, isPending: isSiteCreationPending } = useCreateSites(scopeId, {
    // Counted rather than named once there is more than one: the toast is read after the flyout is gone.
    successToast:
      names.length > 1 ? (
        `${names.length} medias were added to your inventory.`
      ) : (
        <>
          <strong>{names[0]}</strong> was added to your inventory.
        </>
      ),
  })

  /**
   * Turn a pasted list into rows.
   *
   * A paste carrying a single name is left to the browser: intercepting it would break pasting a name
   * into the middle of one already being typed, which is the ordinary use of the field.
   */
  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = parseMediaNames(event.clipboardData.getData('text'))
    if (pasted.length <= 1) {
      return
    }
    event.preventDefault()
    setBatchError('')
    const rows = getValues('sites')
    // The row being pasted into is the active one, and every row the paste opens keeps its environment.
    // A pasted list is one batch of medias; asking for the environment of each in turn would undo the
    // whole point of pasting it.
    const env = rows[index]?.env ?? defaultEnvFor(getValues('product'))
    replace(
      rowsAfterPaste(
        rows,
        index,
        pasted.map((name) => ({ name, env }))
      )
    )
  }

  /** `Input` renders through a `Controller`, so there is no ref to hold onto — the row carries its index. */
  const focusNameAt = (index: number): boolean => {
    const field = rowsRef.current?.querySelector<HTMLInputElement>(`input[data-media-row="${index}"]`)
    field?.focus()
    return !!field
  }

  /**
   * Tab and comma both mean the same thing in a list of names: this one is done, on to the next.
   *
   * Tab because the row carries an environment and a delete button between one name and the next, and a
   * publisher typing twenty medias wants neither — the environment is usually picked once for the batch,
   * and stepping through it on every row is the cost this flyout exists to remove. Nothing leaves the tab
   * order: Shift+Tab still walks back through them, and the trailing free row tabs on to the buttons the
   * ordinary way, because there is where the list ends.
   *
   * Comma because it is already a separator here — it is what a pasted list is split on — so a typed list
   * behaves like a pasted one. A media name never contains one, so nothing legible is being swallowed.
   */
  const handleNameKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    const isPlainTab = event.key === 'Tab' && !event.shiftKey && !event.altKey && !event.metaKey && !event.ctrlKey
    if (event.key !== ',' && !isPlainTab) {
      return
    }
    // A comma never reaches the field, even on the trailing row where there is nowhere to move on to.
    if (event.key === ',') {
      event.preventDefault()
    }
    // An empty row is the end of the list, and Tab out of it should reach the buttons as it always did.
    if (!event.currentTarget.value.trim()) {
      return
    }
    // The row below always exists once this one is named — the trailing free row is kept for it.
    if (focusNameAt(index + 1) && isPlainTab) {
      event.preventDefault()
    }
  }

  const handleCreateSites = async () => {
    if (!scopeId || isSiteCreationPending) return

    try {
      const rows = namedRows(getValues('sites'))
      if (rows.length === 0 || rows.some((row) => !row.env)) return

      const newSites = rows.map((row) => ({
        name: row.name,
        pbjsVariableName: '', // Will use the value from the scope
        enabled: true,
        ...siteProductPayload(getValues('product')),
        env: row.env as SiteEnv,
      }))

      const res = await createSites(newSites)

      onClose(res as Site[])
    } catch (error: any) {
      // The whole batch is refused when any one name is taken, so the rows at fault have to be named:
      // a bare "already exists" against a list of twenty leaves nothing to act on. The names are on the
      // first step, so that is where the user is put back to fix them.
      const conflicts: string[] | undefined = error?.message === 'Conflict data' ? error?.description?.resourceIds : undefined
      if (conflicts?.length) {
        const rows = getValues('sites')
        conflicts.forEach((name) => {
          const index = rows.findIndex((row) => row.name.trim() === name)
          if (index !== -1) {
            setError(`sites.${index}.name`, { type: 'alreadyExists' })
          }
        })
        setBatchError(
          conflicts.length > 1
            ? `${conflicts.length} of these medias already exist in your inventory. No media was added — fix the names below and try again.`
            : 'A media with this name already exists. No media was added — fix the name below and try again.'
        )
        stepHelpers.setStep(MEDIAS_STEP)
      } else {
        setBatchError('')
        toast.alert(error.message)
      }
    }
  }

  /**
   * A media created straight onto One or Node is created on it for good, so the warning the edit flyout
   * gives is given here too. Creating Analytics-only medias commits to nothing and goes through.
   *
   * Nothing is typed back, unlike on the edit flyout. Typing a phrase is what an existing media is worth:
   * there the save turns something already running into something else under it, and the name read back is
   * what proves the right media is being changed. Creating takes nothing away, and the subscription was
   * picked on the first step and read back on the review — a third reading of it would only train people
   * to type past the warning. The batch is one activation, so it is one warning for the whole list.
   */
  const handleAddMedias = async () => {
    if (!isActivatingProduct(undefined, getValues('product'))) {
      await handleCreateSites()
      return
    }
    const confirmLabel = names.length === 1 ? 'Add media' : `Add ${names.length} medias`
    modal.open(SiteSaveConfirmationModal, {
      title: confirmLabel,
      challenges: [{ key: 'product', title: PRODUCT_IS_FINAL_TITLE, description: PRODUCT_IS_FINAL }],
      confirmLabel,
      trackingLabel: `${product}-create-media-confirm`,
      onConfirm: handleCreateSites,
    })
  }

  const getErrorMessage = (errors: FieldErrors<CreateMediaForm>, index: number): string | undefined => {
    const type = errors?.sites?.[index]?.name?.type
    if (type === 'required') {
      return 'A media name is required'
    }
    if (type === 'pattern') {
      return 'A media name cannot only be whitespaces'
    }
    if (type === 'unique') {
      return 'This name is repeated in the list below'
    }
    if (type === 'alreadyExists') {
      return 'A media with this name already exists'
    }
  }

  const envTrackingLabel: Record<SiteEnv, TrackingLabel> = {
    web: `${product}-create-media-env-web-set`,
    ios: 'pbs-create-media-env-ios-set',
    android: 'pbs-create-media-env-android-set',
    amp: 'pbs-create-media-env-amp-set',
  }

  // Every environment stays on screen, the closed ones carrying the rule that closes them — the same
  // cross-sell the product cards do, now that it is the product that narrows the environment. The rule
  // is a hover away rather than printed under the label: it only ever applies to the options that cannot
  // be picked, and inline it made every closed row taller in a list being scanned for the open ones.
  const envOptions = SITE_ENVS.map((env) => ({
    label: SITE_ENV_LABELS[env],
    value: env,
    trackingLabel: envTrackingLabel[env],
    disabled: !!siteEnvDisabledReason(env, selectedProduct),
    disabledReason: siteEnvDisabledReason(env, selectedProduct),
  }))

  // A media is never created on a product this scope cannot enable: on a scope with neither One nor Node
  // the cards all stay closed, and the flyout says so rather than falling back to something unpicked.
  const isProductStepComplete = !selectedProductReason
  // Nor is one ever created without an environment — on Node that is a question the form waits on.
  const areMediasComplete = isValid && medias.length > 0 && medias.every((media) => !!media.env)
  const canAdd = isProductStepComplete && areMediasComplete && !isSiteCreationPending

  // Enter validates whichever step is open, exactly as its button does — including doing nothing when
  // that button is disabled. In the media list Tab and a typed comma still walk from one row to the
  // next, so Enter is free to mean "this list is done" rather than "this row is".
  const handleEnter = validateStepOnEnter(() => {
    if (currentStep === PRODUCT_STEP) {
      if (isProductStepComplete) stepHelpers.goToNextStep()
    } else if (currentStep === MEDIAS_STEP) {
      if (areMediasComplete) stepHelpers.goToNextStep()
    } else if (canAdd) {
      void handleSubmit(handleAddMedias)()
    }
  })

  return (
    <Flyout.Content>
      <Flyout.Title>Add media</Flyout.Title>
      <Flyout.Body ref={bodyRef} tabIndex={-1} className={'outline-none'} onKeyDown={handleEnter}>
        <Box pt={Sizes[8]} pb={Sizes[24]}>
          <MantineStepper active={currentStep - 1}>
            <MantineStepper.Step label="Product" completedIcon={1} />
            <MantineStepper.Step label="Medias" completedIcon={2} />
            <MantineStepper.Step label="Review" completedIcon={3} />
          </MantineStepper>
        </Box>
        {currentStep === PRODUCT_STEP && (
          <div className={'flex flex-col gap-2'}>
            <span className={'text-slate-grey'}>Product</span>
            <SiteProductPicker
              control={control}
              name="product"
              availability={availability}
              staffOnlyProducts={isStaffOnNode ? ['pbs'] : []}
              trackingLabelOf={(p: SiteProduct) => `${product}-create-media-product-${p}-set`}
            />
          </div>
        )}
        {currentStep === MEDIAS_STEP && (
          /* The heading holds the top and stays put; only the rows scroll, so how many medias are being
             added is never scrolled off by the list itself. */
          <div className={'flex h-full min-h-0 flex-col gap-6'}>
            <div className={'flex min-h-0 flex-1 flex-col gap-2'}>
              <div className={'flex shrink-0 flex-col gap-1'}>
                <span className={'text-slate-grey'}>Medias{names.length > 0 && ` (${names.length})`}</span>
                <span className={'text-slate-grey text-sm italic'}>Paste a column from your spreadsheet, or a comma-separated list, to add them all at once.</span>
                {batchError && <span className={'text-alert text-sm'}>{batchError}</span>}
              </div>
              <div ref={rowsRef} className={'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-4 pt-2'}>
                {fields.map((field, index) => (
                  /*
                   * A grid rather than a flex row, because neither field shrinks on its own.
                   *
                   * Both are the same input component underneath, whose wrapper has no width of its own and
                   * so sizes to the intrinsic width of the `input` inside it — about twenty characters. In a
                   * flex row that meant two fields refusing to give up room and the whole line running past
                   * the flyout's edge; wrapping the select in a narrow box did nothing, since the field is
                   * what needed the width, not its container.
                   *
                   * The tracks say it instead: the name takes what is left, `minmax(0, 1fr)` letting it go
                   * below its intrinsic width, and the environment gets the fixed room its options need —
                   * 120px holds "Android", the longest of Web, iOS, Android and AMP, next to the chevron.
                   * `w-full` on each field is what makes it fill its track rather than size itself.
                   */
                  <div key={field.id} className={'grid shrink-0 grid-cols-[minmax(0,1fr)_120px_2.25rem] items-start gap-4'}>
                    <Input
                      className={'w-full min-w-0'}
                      error={getErrorMessage(errors, index)}
                      onChange={() => setBatchError('')}
                      onPaste={handlePaste(index)}
                      onKeyDown={handleNameKeyDown(index)}
                      data-media-row={index}
                      control={control}
                      name={`sites.${index}.name`}
                      label="Media name"
                      rules={{
                        // The trailing row is the free one: it is how the next media is added, not a field left blank.
                        required: fields.length - 1 !== index,
                        pattern: /.*[^ ].*/,
                        validate: {
                          unique: (value, formValues) => {
                            const name = typeof value === 'string' ? value.trim().toLocaleLowerCase() : ''
                            if (!name) return true
                            return formValues.sites.filter((site) => site.name.trim().toLocaleLowerCase() === name).length <= 1
                          },
                        },
                      }}
                    />
                    {/* The environment names are short — "Web", "In-app", "AMP" — so the track above is all
                        this needs, and `w-full` is what holds the field to it. */}
                    <Select className={'w-full min-w-0'} label="Environment" labelIsPlaceholder name={`sites.${index}.env`} control={control} options={envOptions} isClearable={false} />
                    {/* A track of its own, held open whether or not this row can be deleted, so the select
                        beside it keeps the same width down the list and the buttons line up. */}
                    <div className={'pt-1'}>
                      {fields.length > 1 && (
                        <Button disabled={fields.length - 1 === index} onClick={() => remove(index)} variant={'tertiary'} iconName={'delete'} trackingLabel={'pbs-create-media-row-delete'} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {currentStep === REVIEW_STEP && <CreateSiteStepReview product={selectedProduct} medias={medias as { name: string; env: SiteEnv }[]} />}
      </Flyout.Body>
      <Flyout.Actions>
        {stepHelpers.canGoToPrevStep && (
          <Button className="mr-auto" variant={'tertiary'} onClick={stepHelpers.goToPrevStep} trackingLabel={`pbs-create-media-step-${currentStep}-previous`}>
            Previous
          </Button>
        )}
        {currentStep === PRODUCT_STEP ? (
          <Button disabled={!isProductStepComplete} onClick={stepHelpers.goToNextStep} trackingLabel={'pbs-create-media-step-subscription-next'}>
            Next
          </Button>
        ) : currentStep === MEDIAS_STEP ? (
          <Button disabled={!areMediasComplete} onClick={stepHelpers.goToNextStep} trackingLabel={'pbs-create-media-step-medias-next'}>
            Next
          </Button>
        ) : (
          <Button
            disabled={!canAdd}
            iconName={isSiteCreationPending ? 'pubstack' : undefined}
            iconSpin={isSiteCreationPending}
            onClick={handleSubmit(handleAddMedias)}
            trackingLabel="pbs-create-media-save"
          >
            {isSiteCreationPending ? 'Adding medias...' : names.length > 1 ? `Add ${names.length} medias` : 'Add media'}
          </Button>
        )}
      </Flyout.Actions>
    </Flyout.Content>
  )
}
